import type { CaptureOptions, CaptureResult, PostHog } from "posthog-js";
import { CAMPAIGN_PARAM_NAMES, campaignParams } from "./campaignParams.js";
import { safeLocalStorageGet, safeLocalStorageSet } from "./storage.js";

/**
 * Product analytics via PostHog. Production builds served from lorvok.app report
 * to Lorvok's project on the EU cloud (the key below); every other host — `vite`
 * dev, `vite preview`, a Render preview, a LAN address — and the test suite stay
 * inert unless `VITE_POSTHOG_KEY` is set explicitly, so stray clicks never land
 * in the real project. See docs/analytics.md for the event map and the
 * activation funnel.
 *
 * Two modes, decided by the cookie banner (the Privacy Policy describes both):
 * - ACCEPTED: full analytics — PostHog's cookie, the signed-in person
 *   identified with a few profile properties (username, UI language, sign-up
 *   date), D1/D7 retention.
 * - NOT ACCEPTED (declined, or no choice yet): PostHog's cookieless mode
 *   (`cookieless_mode: "on_reject"` + `opt_out_capturing_by_default`). Nothing
 *   is stored in the browser, nobody is identified and no person profile is
 *   made; PostHog counts the visit from a hash it computes on its servers
 *   (the project's "Cookieless server hash mode" must be on, or these events
 *   are dropped). Withdrawing consent via "Cookie settings" returns to it.
 * The choice holds for every tab at once, as posthog-js's own opt-in does
 * (onConsentStorage). Autocapture records no element text in either mode.
 *
 * PostHog's code (~60 KB gzipped) is not in the main bundle: it is imported
 * on demand — at once for a visitor who accepted earlier, otherwise after the
 * page has settled — and calls made while it downloads are queued and
 * replayed in order.
 */

/**
 * Lorvok's PostHog project (EU cloud). A project key (phc_…) is public by
 * design — it ships in every bundle and can only send events — so it is safe to
 * commit.
 */
export const DEFAULT_POSTHOG_KEY = "phc_nvjzsaqS9sE3Atag9RUg9iHuzZkLLoUVs3KS7ro8mZTC";
export const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";
/** `VITE_POSTHOG_KEY` values that switch analytics off (case-insensitive). */
const DISABLED_KEYS = new Set(["off", "false", "0", "none", "disabled"]);

/** The only hosts the baked-in project accepts events from: the production site. */
const PRODUCTION_HOSTNAMES = new Set(["lorvok.app", "www.lorvok.app"]);

/**
 * Which project (if any) this build reports to. An explicit `VITE_POSTHOG_KEY`
 * always wins, on any host — "off"/"false"/"0"/"none"/"disabled" turn analytics
 * off, an empty value counts as unset. Without one, the baked-in project applies
 * only to a production build served from the production site: dev servers,
 * tests, a build previewed on localhost, on *.onrender.com or on a LAN address
 * never report to it. (Moving the site to another domain switches analytics off
 * until that domain is added here.)
 */
export function resolveAnalyticsConfig(env: {
  key?: string;
  host?: string;
  prod: boolean;
  hostname?: string;
}): { key: string | null; host: string } {
  const explicit = env.key?.trim();
  const key = explicit
    ? DISABLED_KEYS.has(explicit.toLowerCase())
      ? null
      : explicit
    : env.prod && PRODUCTION_HOSTNAMES.has(env.hostname?.toLowerCase() ?? "")
      ? DEFAULT_POSTHOG_KEY
      : null;
  return { key, host: env.host?.trim().replace(/\/+$/, "") || DEFAULT_POSTHOG_HOST };
}

const { key: KEY, host: HOST } = resolveAnalyticsConfig({
  key: import.meta.env.VITE_POSTHOG_KEY as string | undefined,
  host: import.meta.env.VITE_POSTHOG_HOST as string | undefined,
  // Truthiness, never `=== true`: under vitest PROD is the string "" or "1".
  prod: Boolean(import.meta.env.PROD),
  hostname: typeof window !== "undefined" ? window.location.hostname : undefined
});

/** True when this build reports to a PostHog project (the banner is only needed then). */
export const analyticsConfigured = KEY !== null;

export type AnalyticsConsent = "granted" | "denied";
const CONSENT_KEY = "lorvok:analyticsConsent";
/** Window event that asks the cookie banner to reopen (footer "Cookie settings"). */
export const COOKIE_SETTINGS_EVENT = "lorvok:cookie-settings";

// PostHog has been started (in either mode); calls queue until its code loads.
let initialized = false;
// The visitor accepted analytics: full mode (identify, profiles, cookies). Kept in
// step with the stored choice, which other tabs can change (see onConsentStorage).
let consented = false;
// The `storage` listener that follows choices made in other tabs is registered.
let listening = false;
// The loaded PostHog instance, and the calls waiting for it to load.
let client: PostHog | null = null;
let pending: Array<(ph: PostHog) => void> = [];
let loadFailed = false;

// A stalled download must not grow the queue for the rest of the session.
const MAX_PENDING_CALLS = 200;

/** Runs `call` against PostHog as soon as its code has loaded, in call order. */
function withPostHog(call: (ph: PostHog) => void): void {
  if (client) call(client);
  else if (!loadFailed && pending.length < MAX_PENDING_CALLS) pending.push(call);
}
/** Person properties sent with identify: `set` overwrites, `setOnce` keeps the first value. */
export interface IdentityProps {
  set?: Record<string, unknown>;
  setOnce?: Record<string, unknown>;
}
type Identity = { userId: string; props?: IdentityProps };
// The last identity the app reported, applied once consent arrives later.
let pendingIdentity: Identity | null = null;
// The identity last sent to PostHog in full mode, so the same one is not sent twice.
let identifiedAs: Identity | null = null;

/**
 * posthog-js's distinct_id while it counts cookielessly (its
 * COOKIELESS_SENTINEL_VALUE); the server swaps it for its daily hash.
 */
const COOKIELESS_DISTINCT_ID = "$posthog_cookieless";
// The page on screen, so the page the visitor accepted on still gets its $pageview.
let lastPage: string | null = null;
let lastPageProps: Record<string, unknown> | undefined;
// The landing URL's campaign tags (utm_source, gclid, …), read when the app boots.
// PostHog starts later and reads them from the address bar, which the visitor may
// have left by then, so prepareEvent adds them to EVERY event of this page load —
// SDK-emitted ones ($autocapture, $identify) too — as posthog-js itself does while
// they are still in the URL. Every campaign key is written, the ones this landing
// lacks as null, so an earlier landing's tags in the same tab never mix in. (Not
// register_for_session: posthog-js 1.434 unregisters those keys — its own
// URL-derived ones included — on a session rotation, and a consent switch resets
// them.)
const landingCampaign: Record<string, string | null> = (() => {
  const found = typeof window === "undefined" ? {} : campaignParams(window.location.search);
  if (Object.keys(found).length === 0) return {};
  return Object.fromEntries(CAMPAIGN_PARAM_NAMES.map((name) => [name, found[name] ?? null]));
})();

/** `ph.capture`, passing capture options only when there are any. */
function capture(ph: PostHog, event: string, props?: Record<string, unknown>, options?: CaptureOptions): void {
  if (options) ph.capture(event, props, options);
  else ph.capture(event, props);
}

// track() calls made before PostHog starts — a cookieless visitor's starts up to
// 3 s after load, so a cold /play load's novel_opened lands here — sent once it
// does, stamped with the time they happened. Capped like the pending queue.
const MAX_EARLY_EVENTS = 50;
let earlyEvents: Array<{ event: AnalyticsEvent; props?: Record<string, unknown>; at: Date }> = [];

/**
 * Every event the app emits. The core activation funnel is
 * `generation_started` → `ending_reached` → `generation_started` (a 2nd time) →
 * a later-day return (D1/D7 retention, computed by PostHog from any event once
 * the person is identified). The rest give broad site coverage.
 */
export type AnalyticsEvent =
  | "generation_started"
  | "generation_completed"
  | "generation_failed"
  | "novel_opened"
  | "ending_reached"
  | "novel_published"
  | "novel_liked"
  | "avatar_generated"
  | "choice_made"
  | "generation_cancelled"
  | "generation_start_failed"
  | "login_completed"
  | "sign_up_started"
  | "sign_up_completed"
  | "signup_prompt_shown"
  | "share_clicked"
  | "consent_choice"
  | "checkout_started"
  | "checkout_completed"
  | "catalog_filter_changed";

/** The visitor's stored analytics choice, or null when they have not chosen yet. */
export function getAnalyticsConsent(): AnalyticsConsent | null {
  const value = safeLocalStorageGet(CONSENT_KEY);
  return value === "granted" || value === "denied" ? value : null;
}

/** Records the visitor's choice and applies it immediately. */
export function setAnalyticsConsent(consent: AnalyticsConsent): void {
  safeLocalStorageSet(CONSENT_KEY, consent);
  if (!KEY || typeof window === "undefined") return;
  consented = consent === "granted";
  if (!initialized) {
    // Straight from the click, not only via the stored choice: storage may be blocked.
    startPostHog();
    return;
  }
  // Decided by PostHog's own state, not by what this tab believed: another tab's
  // choice may already have switched it (see onConsentStorage).
  withPostHog(consented ? enterFullMode : leaveFullModeIfNeeded);
}

/**
 * Cookieless → full mode, then the identity. The page on screen was already
 * counted cookielessly.
 */
function enterFullMode(ph: PostHog): void {
  // Already in full mode — accepted earlier, here or in another tab (posthog-js
  // keeps its opt-in in localStorage, shared by every tab): nothing to redo.
  // Opting in again would also save this tab's in-memory identity over the one
  // the other tab stored.
  if (!ph.has_opted_in_capturing()) ph.opt_in_capturing({ captureEventName: false });
  applyIdentity(ph);
}

/**
 * Takes PostHog back to cookieless mode unless it is there already — judged by
 * PostHog's real state: its opt-in, and the distinct_id this tab holds in memory.
 * The id matters on its own: when another tab withdraws, posthog-js here reads
 * the shared opt-out at once, but keeps the full-mode id (possibly the account's)
 * until it is reset.
 */
function leaveFullModeIfNeeded(ph: PostHog): void {
  identifiedAs = null;
  if (!ph.has_opted_in_capturing() && ph.get_distinct_id() === COOKIELESS_DISTINCT_ID) return;
  // reset() wipes PostHog's stored consent along with the identity, so it must
  // run first; the opt-out after it switches PostHog back to cookieless mode
  // and deletes its cookie and localStorage entry.
  ph.reset();
  ph.opt_out_capturing();
}

/** Identifies the signed-in person — only in full mode, by our choice AND PostHog's. */
function applyIdentity(ph: PostHog): void {
  const identity = pendingIdentity;
  if (!consented || !identity || identity === identifiedAs || !ph.has_opted_in_capturing()) return;
  identifiedAs = identity;
  ph.identify(identity.userId, identity.props?.set, identity.props?.setOnce);
}

/** Follows choices made in other tabs from now on (idempotent). */
function listenForConsentChanges(): void {
  if (listening) return;
  listening = true;
  window.addEventListener("storage", onConsentStorage);
}

/**
 * The choice changed in another tab (a `storage` event never reaches the tab
 * that wrote it). posthog-js keeps its opt-in in localStorage, shared by every
 * tab, and re-reads it on every event, so the other tab's switch already moves
 * this tab's PostHog: a grant takes it to full mode at its next event (adopting
 * the identity the other tab stored), a withdrawal back to cookieless mode —
 * but with this tab's full-mode id still in memory. This listener brings the
 * rest of the layer along:
 * - granted: this tab counts as consented (identify, sign-out, a later Decline
 *   here all act on full mode). PostHog is not opted in again from here — the
 *   tab that granted does that, and doing it twice would save this tab's
 *   in-memory identity over the one that tab stored.
 * - denied, or the choice cleared: this tab leaves full mode as well (reset +
 *   opt-out), dropping the id it holds.
 * Before PostHog starts here, only the flag changes; the start reads the stored
 * choice again anyway (initPostHog).
 */
function onConsentStorage(event: StorageEvent): void {
  // key null: another tab cleared localStorage, our choice with it.
  if (event.key !== CONSENT_KEY && event.key !== null) return;
  const granted = event.key === CONSENT_KEY && event.newValue === "granted";
  if (granted && consented) return;
  consented = granted;
  if (!initialized) return;
  withPostHog(granted ? applyIdentity : leaveFullModeIfNeeded);
}

/** Asks the cookie banner to reopen so the visitor can change their choice. */
export function openCookieSettings(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}

// Any email address anywhere in an event (element text, a title attribute, a
// URL) is replaced before the event leaves the browser. Known spots are also
// marked `ph-no-capture`; this is the net for the ones nobody marked.
// Also the URL-encoded forms (`%40`, `%2540`, …): a campaign tag kept in the
// address bar can carry one into $current_url.
const EMAIL_PATTERN = /[A-Z0-9._%+-]+(?:@|%(?:25)*40)[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}/gi;
const ENCODED_AT = /%(?:25)*40/i;

function scrubEmails(value: unknown, depth = 0): unknown {
  if (typeof value === "string") {
    return value.includes("@") || ENCODED_AT.test(value) ? value.replace(EMAIL_PATTERN, "[email]") : value;
  }
  if (depth > 8 || value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => scrubEmails(item, depth + 1));
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) out[key] = scrubEmails(item, depth + 1);
  return out;
}

/** before_send hook: strips email addresses from an event's properties. */
export function scrubEventEmails(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  return {
    ...event,
    properties: scrubEmails(event.properties) as CaptureResult["properties"],
    ...(event.$set ? { $set: scrubEmails(event.$set) as CaptureResult["$set"] } : {}),
    ...(event.$set_once ? { $set_once: scrubEmails(event.$set_once) as CaptureResult["$set_once"] } : {})
  };
}

/**
 * posthog-js copies `document.title` into the `title` of every `$pageview`. The
 * tab's title is the novel's or the author's name (lib/pageMeta.ts) — a private
 * draft's included, and titles are novel text, which no property may carry —
 * and nothing reads it: pages are told apart by `$current_url` and `page`.
 */
function withoutPageTitle(event: CaptureResult | null): CaptureResult | null {
  if (!event?.properties || !("title" in event.properties)) return event;
  const { title: _title, ...properties } = event.properties;
  void _title;
  return { ...event, properties };
}

/** Ids a cookieless tab never sends (it has no user, and no session of its own). */
const FULL_MODE_ONLY_PROPERTIES = ["$user_id", "$session_id", "$window_id"];

/**
 * The before_send hook. Adds the landing's campaign tags (see `landingCampaign`),
 * strips emails and the page title from every event, and keeps cookieless events (a visitor who has
 * not accepted) PERSONLESS and ANONYMOUS:
 * - posthog-js marks them `$process_person_profile: true` and sends its
 *   initial-properties `$set`, which would make PostHog keep a profile per
 *   server-side hash — the Privacy Policy promises none. So those get
 *   `$process_person_profile: false` and the `$set` is dropped.
 * - a tab that another tab's withdrawal has just switched to cookieless mode
 *   still holds its full-mode ids in memory until onConsentStorage resets it;
 *   an event in between would carry them. So every cookieless event goes out
 *   the way a clean cookieless tab sends it: the sentinel distinct_id, no
 *   device id, and no user or session ids.
 */
export function prepareEvent(rawEvent: CaptureResult | null): CaptureResult | null {
  const event = withoutPageTitle(rawEvent);
  const tagged =
    event && Object.keys(landingCampaign).length > 0
      ? { ...event, properties: { ...event.properties, ...landingCampaign } }
      : event;
  const scrubbed = scrubEventEmails(tagged);
  if (!scrubbed || scrubbed.properties?.$cookieless_mode !== true) return scrubbed;
  if (scrubbed.event === "$set" || scrubbed.event === "$identify") return null;
  const { $set: _set, $set_once: _setOnce, ...rest } = scrubbed;
  void _set;
  void _setOnce;
  const properties = { ...scrubbed.properties };
  for (const key of FULL_MODE_ONLY_PROPERTIES) delete properties[key];
  return {
    ...rest,
    properties: { ...properties, distinct_id: COOKIELESS_DISTINCT_ID, $device_id: null, $process_person_profile: false }
  };
}

/**
 * Starts analytics on boot. A visitor who accepted earlier gets full mode at
 * once; everyone else gets cookieless mode once the page has settled (PostHog's
 * code then loads off the critical path). Needs a key; safe to call repeatedly.
 */
export function initAnalytics(): void {
  if (initialized || !KEY || typeof window === "undefined") return;
  // From boot: a choice made in another tab before PostHog starts here counts too.
  listenForConsentChanges();
  if (getAnalyticsConsent() === "granted") {
    consented = true;
    startPostHog();
    return;
  }
  const start = (): void => startPostHog();
  const idle = (window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (idle) idle(start, { timeout: COOKIELESS_START_DELAY_MS });
  else window.setTimeout(start, COOKIELESS_START_DELAY_MS);
}

/** Longest wait before a cookieless visitor's PostHog starts. */
const COOKIELESS_START_DELAY_MS = 3_000;

/** Initializes PostHog once, in the mode `consented` says. */
function startPostHog(): void {
  if (initialized || !KEY || typeof window === "undefined") return;
  const key = KEY;
  initialized = true;
  listenForConsentChanges();
  // init, the mode and the identity (initPostHog), before anything is sent.
  withPostHog((ph) => initPostHog(ph, key));
  // The page on screen when PostHog starts; later pages come from trackPageView.
  const page = lastPage;
  const pageProps = lastPageProps;
  if (page) withPostHog((ph) => capture(ph, "$pageview", { ...pageProps, page }));
  // Then the events from before the start, at their own times, in the mode
  // PostHog starts in (nothing left the browser before that).
  const early = earlyEvents;
  earlyEvents = [];
  for (const { event, props, at } of early) {
    withPostHog((ph) => capture(ph, event, props, { timestamp: at }));
  }
  void import("posthog-js")
    .then(({ posthog }) => {
      client = posthog;
      const queued = pending;
      pending = [];
      for (const call of queued) call(posthog);
    })
    .catch(() => {
      // Analytics are best-effort: a blocked or failed download just drops them.
      loadFailed = true;
      pending = [];
    });
}

/**
 * Attributes autocapture never records, on any element of a click's chain.
 * `style` carries image URLs (background-image); the rest are the standard
 * attributes that hold human-readable text — a button's aria-label or title can
 * name a novel, a scene or a person just as its text can (see mask_all_text).
 * Classes, ids, hrefs and data-* attributes are still recorded: our own
 * markup, which must never carry prose.
 */
const TEXT_ATTRIBUTES = [
  "style",
  "aria-label",
  "aria-description",
  "aria-roledescription",
  "aria-valuetext",
  "aria-placeholder",
  "title",
  "alt",
  "placeholder",
  "label",
  "value",
  "download"
];

function initPostHog(posthog: PostHog, key: string): void {
  posthog.init(key, {
    api_host: HOST,
    // This is a useState-routed SPA: most navigations don't change the URL, so
    // we send $pageview manually (see trackPageView) instead of on load only.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: {
      // Clicks on controls only. posthog-js matches these against the clicked
      // element AND its ancestors, so the icon inside a button still counts,
      // while a tap on the player's text sends nothing. `form` is here for
      // submit events (it also admits clicks inside a form); $rageclick goes
      // through the same filter.
      css_selector_allowlist: ["a", "button", "input", "select", "textarea", "label", "form", "summary"],
      // A custom list REPLACES posthog-js's default one, whose two entries are
      // repeated. <option> clicks carry asset ids and scene titles; the
      // <select>'s change event still goes.
      css_selector_ignorelist: [".ph-no-autocapture", "[data-ph-no-autocapture]", "option"],
      element_attribute_ignorelist: TEXT_ATTRIBUTES,
      // Pinned off: it would send the text a reader selects and copies.
      capture_copied_text: false
    },
    // No element text at all ($el_text, and the text in $elements_chain), on
    // every element of every autocaptured event: choice labels, scene and novel
    // titles, names inside controls never leave the browser, whatever the
    // markup. Clicks keep their tag names, classes, ids and hrefs, which is what
    // actions and insights select on. (Not mask_all_element_attributes: that
    // also drops classes, ids and hrefs, leaving only tag names.)
    mask_all_text: true,
    // Heatmaps ignore ph-no-capture and consent, and the Privacy Policy covers
    // pages and clicks only.
    capture_heatmaps: false,
    // Pinned in code so a project-settings toggle cannot switch these on. Web
    // vitals stay (the Web Vitals tab); network timing and console logs only
    // feed session replay, which is off.
    capture_performance: { web_vitals: true, network_timing: false },
    enable_recording_console_log: false,
    capture_dead_clicks: false,
    // Unused widgets: never load their code.
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    // No /flags requests (no feature flags are used). NOT advanced_disable_flags:
    // that one also stops the remote config autocapture waits for.
    advanced_disable_feature_flags: true,
    // Keep PostHog's cookie on the site's own host: the default scopes it to
    // .lorvok.app, which sends it along with every img.lorvok.app image request.
    cross_subdomain_cookie: false,
    persistence: "localStorage+cookie",
    // Anonymous visitors stay personless; a person exists once identify() runs.
    person_profiles: "identified_only",
    // Everyone starts opted out; opted out means COOKIELESS mode (nothing stored
    // in the browser, a server-side hash instead of a distinct_id), and only an
    // accepted banner opts in to cookies + identify.
    opt_out_capturing_by_default: true,
    cookieless_mode: "on_reject",
    // Opting out also deletes PostHog's cookie and localStorage entry.
    opt_out_persistence_by_default: true,
    // The Privacy Policy covers usage events only: no session replay, and no
    // error reports linked to the account (crashes go to our own error sink).
    // Code wins over the project settings for both.
    disable_session_recording: true,
    capture_exceptions: false,
    // Supabase sign-in links land on /signin#access_token=…&refresh_token=…;
    // never let those tokens into $current_url or $initial_current_url.
    disable_capture_url_hashes: true,
    // An explicit key used from a local dev server marks that person as a test
    // user, so PostHog's "filter out internal and test users" drops it.
    internal_or_test_user_hostname: /^(localhost|127\.0\.0\.1)$/,
    before_send: prepareEvent
  });
  // Our consent key is the source of truth, read again now: another tab may have
  // chosen while this one waited to start (the idle delay, the download), and its
  // PostHog has already switched the opt-in every tab shares — judging by a
  // stale flag here would undo that for all of them. The flag stands only when
  // storage is unreadable. PostHog's own stored opt-in/out then follows ours: a
  // withdrawal in an earlier visit left an opt-out behind, and an old opt-in
  // must not survive our consent being cleared.
  const stored = getAnalyticsConsent();
  if (stored) consented = stored === "granted";
  if (consented) enterFullMode(posthog);
  else leaveFullModeIfNeeded(posthog);
}

/**
 * Ties events to a stable person (D1/D7 retention) and keeps a few profile
 * properties on it. Applied only in full (accepted) mode — cookieless visitors
 * are never identified.
 */
export function identifyUser(userId: string, props?: IdentityProps): void {
  pendingIdentity = { userId, props };
  if (!initialized || !consented) return;
  withPostHog(applyIdentity);
}

/** Clears the identified person on sign-out so the next user starts clean. */
export function resetAnalytics(): void {
  pendingIdentity = null;
  if (!initialized) return;
  withPostHog((ph) => {
    identifiedAs = null;
    // Only after an identified session: resetting an anonymous visitor would mint
    // a new distinct_id on every page load and inflate the unique-visitor count.
    // Checked whatever this tab believed about consent: another tab's choice can
    // have taken its PostHog to full mode and back, leaving an identified id.
    if (ph.get_property("$user_state") !== "identified") return;
    ph.reset();
    // reset() also clears PostHog's stored opt-in, which leaves the signed-out
    // visitor half in cookieless mode (cookieless events on a stored id), so
    // opt straight back in: a fresh anonymous id, still in full mode. Without
    // consent, back to clean cookieless mode instead — which also deletes the
    // anonymous id reset() just stored.
    if (consented) ph.opt_in_capturing({ captureEventName: false });
    else ph.opt_out_capturing();
  });
}

/** Manual SPA pageview — `page` is the app's route key (e.g. "catalog"). */
export function trackPageView(page: string, props?: Record<string, unknown>): void {
  lastPage = page;
  lastPageProps = props;
  if (!initialized) return;
  withPostHog((ph) => capture(ph, "$pageview", { ...props, page }));
}

/**
 * Captures a product event, in whichever mode analytics runs. Before PostHog
 * starts the event waits (see `earlyEvents`); without a project it is dropped.
 */
export function track(event: AnalyticsEvent, props?: Record<string, unknown>): void {
  if (!KEY || typeof window === "undefined") return;
  if (!initialized) {
    if (earlyEvents.length < MAX_EARLY_EVENTS) earlyEvents.push({ event, props, at: new Date() });
    return;
  }
  withPostHog((ph) => capture(ph, event, props));
}
